import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import BrandingsService, { IBrandingSpotApi } from '@services/BrandingsService';
import {
  BrandingDriftState,
  computeBrandingSpotDrift,
  computeTriggerDrift,
  IBrandingDrift,
  ITriggerDrift,
} from '@shared/utils/mapTemplateDrift';
import { resolveBuildLayout } from '@shared/utils/buildLayout';
import {
  assistantMapKey,
  matchesAssistantMap,
} from '@shared/utils/assistantMap';
import type { IAssistantScript } from '@shared/interfaces/assistantScript';

import { useEditorAiData } from './AiScripts/EditorAiDataContext';
import { collectSpawnedRegionNames } from './AiScripts/useRegionIndex';
import { collectStandingRegions } from './mapEditorUnreal';

export interface DriftRollup {
  state: BrandingDriftState;
  missingSpots: number;
  orphanedSpots: number;
  newScripts: number;
  orphanedScripts: number;
  hasDrift: boolean;
}

export interface EditorDriftData {
  loading: boolean;
  hasTemplate: boolean;
  templateVersion?: string;
  spotDrift: IBrandingDrift<IBrandingSpotApi>;
  triggerDrift: ITriggerDrift<IAssistantScript>;
  rollup: DriftRollup;
  refreshSpots: () => Promise<void>;
}

const EditorDriftContext = createContext<EditorDriftData | null>(null);

interface DriftProviderProps {
  children: ReactNode;
}

export const DriftProvider = ({ children }: DriftProviderProps) => {
  const {
    currentRoom,
    currentBrandingId,
    brandingSpots,
    setBrandingSpots,
    dynamicAiTriggers,
    pixelStreamingClient,
    realtimeEntities,
    currentGeometryRooms,
    currentGeometryStartRoom,
  } = useMytaverseEvent();
  const { scripts } = useEditorAiData();

  const mapId = currentRoom?.map?.mapId;
  // Assistant content is keyed by the map's build-stable ueIdentifier, never by
  // its renameable display name (MV-1891).
  const map = currentRoom?.map;
  const isMapScoped = Boolean(assistantMapKey(map));

  // The layout comes from the build being streamed, not from the persistent
  // ACTIVE map template: a build upload re-activates that row by checksum, so
  // building an older build rolled the layout back under an open editor.
  const activeTemplate = useMemo(
    () =>
      resolveBuildLayout({
        client: pixelStreamingClient,
        mapUeIdentifier: map?.ueIdentifier,
        mapId,
      }),
    [pixelStreamingClient, map?.ueIdentifier, mapId],
  );

  // "New in version" / "removed" compare two consecutive snapshots of one map;
  // reading a single build there is nothing to compare against.
  // `computeBrandingSpotDrift` already handles a missing previous template — the
  // badges simply do not render.
  const previousTemplate = null;

  // The layout is derived synchronously from the build record, so nothing ever loads.
  // Kept on the context rather than dropped, because `useDrift` is mocked against this
  // shape across the editor tests and consumers read it to gate their own spinners.
  const loading = false;

  const spots = useMemo<IBrandingSpotApi[]>(
    () => (Array.isArray(brandingSpots) ? brandingSpots : []),
    [brandingSpots],
  );

  const spotDrift = useMemo(
    () =>
      computeBrandingSpotDrift<IBrandingSpotApi>(
        spots,
        activeTemplate,
        previousTemplate,
      ),
    [spots, activeTemplate, previousTemplate],
  );

  // Match the AI tree's scope filter (aggregate.ts → filterByScope) so the
  // drift groups agree with the Scripted / No-script groups already rendered.
  const scopedScripts = useMemo(
    () =>
      scripts.filter((script) => {
        if (isMapScoped && !matchesAssistantMap(script.mapName, map)) {
          return false;
        }
        if (currentBrandingId && script.brandingId !== currentBrandingId) {
          return false;
        }
        return true;
      }),
    [scripts, isMapScoped, map, currentBrandingId],
  );

  // Dynamic (runtime-discovered) triggers, the trigger regions the web spawned
  // and saved, and standing regions from a repeated module placement were
  // never part of the (static) build template, so none of them must be
  // flagged "orphaned — removed from build" the moment a script is bound to
  // one. See computeTriggerDrift's dynamicTriggerNames doc. Saved spawned
  // names only: a script cannot be bound to an unsaved one, and the moment an
  // entity is deleted its name leaves this list and the script does read as
  // orphaned. Standing regions come from CURRENT_GEOMETRY_LIST rather than
  // the registry, so there is no "saved" concept for them — a module placed
  // more than once has each copy's regions renamed `<roomKey>#<suffix>` by
  // UE, and useRegionIndex (the trigger picker) already treats these as
  // selectable; without folding them in here too, any script bound to one
  // could never match this side and would always read as orphaned.
  //
  // The *Key strings below exist only to give useMemo a stable primitive to
  // compare — the registry/room objects change identity on every transform
  // edit, and a fresh array on every render would recompute the drift and
  // re-render every consumer for an edit that renamed nothing. They are never
  // split back apart: a trigger name can itself contain a space ("Hall A"),
  // so join-then-split on ' ' would silently fracture it into two tokens.
  const spawnedRegionNames = collectSpawnedRegionNames(
    Array.isArray(realtimeEntities) ? realtimeEntities : [],
  );
  const spawnedRegionNamesKey = spawnedRegionNames.join(' ');
  const standingRegionNames = collectStandingRegions([
    ...(currentGeometryRooms ?? []),
    ...(currentGeometryStartRoom ? [currentGeometryStartRoom] : []),
  ])
    .map((region) => region.regionName)
    .filter(Boolean);
  const standingRegionNamesKey = standingRegionNames.join(' ');
  const dynamicTriggerNames = useMemo(
    () => [
      ...dynamicAiTriggers.map((trigger) => trigger.triggerName),
      ...spawnedRegionNames,
      ...standingRegionNames,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dynamicAiTriggers, spawnedRegionNamesKey, standingRegionNamesKey],
  );

  const triggerDrift = useMemo(
    () =>
      computeTriggerDrift<IAssistantScript>(
        scopedScripts,
        activeTemplate,
        previousTemplate,
        { dynamicTriggerNames },
      ),
    [scopedScripts, activeTemplate, previousTemplate, dynamicTriggerNames],
  );

  const refreshSpots = useCallback(async () => {
    if (!currentBrandingId) {
      return;
    }
    try {
      const refreshed = await BrandingsService.getBrandingSpots(
        currentBrandingId,
        { limit: 1000 },
      );
      setBrandingSpots(refreshed.spots ?? []);
    } catch (error) {
      console.error(
        `Failed to refresh configuration spots for drift: ${
          (error as Error).message
        }`,
        { brandingId: currentBrandingId },
      );
    }
  }, [currentBrandingId, setBrandingSpots]);

  const hasTemplate = Boolean(activeTemplate);

  const rollup = useMemo<DriftRollup>(() => {
    let state: BrandingDriftState;
    if (!hasTemplate) {
      state = 'unknown';
    } else if (spotDrift.orphaned > 0 || triggerDrift.orphaned > 0) {
      state = 'outdated';
    } else if (spotDrift.missing > 0 || triggerDrift.newCount > 0) {
      state = 'behind';
    } else {
      state = 'up-to-date';
    }
    return {
      state,
      missingSpots: spotDrift.missing,
      orphanedSpots: spotDrift.orphaned,
      newScripts: triggerDrift.newCount,
      orphanedScripts: triggerDrift.orphaned,
      hasDrift: state === 'behind' || state === 'outdated',
    };
  }, [hasTemplate, spotDrift, triggerDrift]);

  const value = useMemo<EditorDriftData>(
    () => ({
      loading,
      hasTemplate,
      templateVersion: activeTemplate?.version,
      spotDrift,
      triggerDrift,
      rollup,
      refreshSpots,
    }),
    [
      loading,
      hasTemplate,
      activeTemplate,
      spotDrift,
      triggerDrift,
      rollup,
      refreshSpots,
    ],
  );

  return (
    <EditorDriftContext.Provider value={value}>
      {children}
    </EditorDriftContext.Provider>
  );
};

export const useDrift = (): EditorDriftData => {
  const ctx = useContext(EditorDriftContext);
  if (!ctx) {
    throw new Error('useDrift must be used inside DriftProvider');
  }
  return ctx;
};
